import axios from "axios";
import { tracker } from "@openreplay/tracker";
import trackerAssist from "@openreplay/tracker-assist";
import trackerProfiler from "@openreplay/tracker-profiler";
import type { AnyAction, Middleware } from "@reduxjs/toolkit";
import firebase from "firebase/app";
import type { ObservabilityContext } from "ts/other";

const PROJECT_KEY =
  import.meta.env.VITE_OR_PROJECT_KEY ?? "KiB1htTMRO1JnOisi7rm";
const INGEST_POINT =
  import.meta.env.VITE_OR_INGEST_POINT ??
  "https://openreplay.ffair.live/ingest";

// Always on for these deployed envs (matches `VITE_PROJECT_ID` from
// envs/.env.*) — everything except local dev and `ffair-sandbox`.
const ACTIVE_PROJECT_IDS = ["ffair-live", "ffair-preproduction"];

const isActivateOpenReplay = (): boolean => {
  if (!ACTIVE_PROJECT_IDS.includes(import.meta.env.VITE_PROJECT_ID ?? "")) {
    return false;
  }
  if (!PROJECT_KEY) {
    console.warn("OpenReplay disabled: VITE_OR_PROJECT_KEY missing");
    return false;
  }
  return true;
};

// Strip auth tokens / API keys / emails from arbitrary URL strings so they
// don't end up attached to reported errors. Keeps the path, replaces only
// the value portion of sensitive query params.
const SENSITIVE_QUERY_PARAMS = /([?&](?:token|access[-_]token|id[-_]token|key|auth|password|email)=)[^&#]+/gi;

export const scrubUrl = (url: string | undefined): string | undefined => {
  if (!url) return url;
  return url.replace(SENSITIVE_QUERY_PARAMS, "$1***");
};

// =============================================================================
// Network payload capture (self-hosted, free tier — no per-event billing, so
// request/response bodies are captured in full). `ignoreHeaders` already
// strips cookie/set-cookie/authorization by default; this sanitizer redacts
// the same class of secrets when they show up as JSON body fields instead
// (login password, token refresh responses, payment details) so full-payload
// capture doesn't leak credentials into the replay.
// =============================================================================

// Matches body fields AND header names — reused for both below. Belt-and-
// suspenders for headers specifically: `ignoreHeaders` is the primary
// defense, this catches any credential-shaped header someone forgets to
// add there (this is exactly how the `access-token` header — this app's
// actual bearer-token header, not named "authorization" — leaked into
// captured requests before it was added to `ignoreHeaders`).
const SENSITIVE_KEY = /password|token|secret|api[-_]?key|authorization|cookie/i;

// `seen` guards against circular references. Network bodies come from
// `JSON.parse`, which can never produce a cycle — but `redactBody` also
// redacts live Redux action objects (`openReplayReduxMiddleware`), which
// can legitimately contain one (e.g. a self-referencing class instance).
// Without this, one circular action would stack-overflow on every single
// dispatch of that action type, forever.
export const redactBody = (
  value: unknown,
  seen: WeakSet<object> = new WeakSet()
): unknown => {
  if (Array.isArray(value)) return value.map((v) => redactBody(v, seen));
  if (value && typeof value === "object") {
    if (seen.has(value)) return "[Circular]";
    seen.add(value);
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, v]) => [
        key,
        SENSITIVE_KEY.test(key) ? "***" : redactBody(v, seen),
      ])
    );
  }
  return value;
};

type NetworkRequestResponse = {
  readonly status: number;
  readonly method: string;
  url: string;
  request: { body: string | null; headers: Record<string, string> };
  response: { body: unknown; headers: Record<string, string> };
};

export const redactHeaders = (
  headers: Record<string, string>
): Record<string, string> =>
  Object.fromEntries(
    Object.entries(headers).map(([key, value]) => [
      key,
      SENSITIVE_KEY.test(key) ? "***" : value,
    ])
  );

const networkSanitizer = (
  data: NetworkRequestResponse
): NetworkRequestResponse => {
  data.url = scrubUrl(data.url) ?? data.url;
  data.request.headers = redactHeaders(data.request.headers);
  data.response.headers = redactHeaders(data.response.headers);

  if (data.request.body) {
    try {
      data.request.body = JSON.stringify(
        redactBody(JSON.parse(data.request.body))
      );
    } catch {
      // Not JSON (e.g. multipart/form-data upload) — leave as-is.
    }
  }

  if (data.response.body && typeof data.response.body === "object") {
    data.response.body = redactBody(data.response.body);
  }

  return data;
};

// =============================================================================
// Noise filters — single source of truth for errors we don't want reported.
// Applied in `openReplayError` before an error reaches OpenReplay.
//
// Adding a message filter is appropriate ONLY when:
//   1. Produced by a library / browser / extension, not our own code.
//   2. Self-recovers or is a no-op (we / the user can't act on it).
//   3. Verified not to mask any real failure mode.
//
// Reference (Sentry's de-facto curated list):
//   https://docs.sentry.io/platforms/javascript/configuration/filtering/
// =============================================================================

type NoiseFilter = {
  /** Which field of the event to test against. */
  target: "url" | "message";
  pattern: RegExp;
  /**
   * Drop only when the resource status code equals this. Used for background
   * aborts (status=0 = "browser cancelled the request") so real failures from
   * the same URL — expired auth, quota, outage — still surface.
   */
  statusCode?: number;
};

const NOISE_FILTERS: NoiseFilter[] = [
  // -- Health-check probes (network-status.store.ts) -------------------------
  { target: "url", pattern: /^https:\/\/firestore\.googleapis\.com\/?$/ },
  { target: "url", pattern: /^https:\/\/firebasestorage\.googleapis\.com\/?$/ },
  { target: "url", pattern: /^https:\/\/identitytoolkit\.googleapis\.com\/?$/ },
  {
    target: "url",
    pattern: /^https:\/\/[^/]*cloudfunctions\.net\/__health_probe__/,
  },
  { target: "url", pattern: /^https:\/\/js\.stripe\.com\/v3\/?$/ },
  { target: "url", pattern: /^https:\/\/proxy\.ffair\.show\/health/ },
  { target: "url", pattern: /\/health(\?|$)/ },
  { target: "url", pattern: /\/robots\.txt\?_=/ },

  // -- Firebase background channels (status=0 only) --------------------------
  {
    target: "url",
    pattern: /\/google\.firestore\.v1\.Firestore\/(Listen|Write)\/channel/,
    statusCode: 0,
  },
  {
    target: "url",
    pattern: /^https:\/\/securetoken\.googleapis\.com\/v1\/token/,
    statusCode: 0,
  },
  {
    target: "message",
    pattern: /^(?:XHR|Fetch) error \w+ https?:\/\/[^\s]*\/google\.firestore\.v1\.Firestore\/(?:Listen|Write)\/channel/i,
  },
  {
    target: "message",
    pattern: /^(?:XHR|Fetch) error \w+ https?:\/\/securetoken\.googleapis\.com\/v1\/token/i,
  },
  { target: "message", pattern: /^Missing or insufficient permissions\.?$/i },
  {
    target: "message",
    pattern: /A network error \(such as timeout, interrupted connection or unreachable host\) has occurred/i,
  },

  // -- Browser quirks --------------------------------------------------------
  { target: "message", pattern: /ResizeObserver loop limit exceeded/i },
  {
    target: "message",
    pattern: /ResizeObserver loop completed with undelivered notifications/i,
  },
  { target: "message", pattern: /^Script error\.?$/i },

  // -- Browser-translate extensions (Chrome / Edge) --------------------------
  { target: "message", pattern: /Failed to execute 'removeChild' on 'Node'/i },
  { target: "message", pattern: /Failed to execute 'insertBefore' on 'Node'/i },
  {
    target: "message",
    pattern: /The node to be removed is not a child of this node/i,
  },
  {
    target: "message",
    pattern: /The provided callback is no longer runnable/i,
  },

  // -- Password-manager extensions ------------------------------------------
  {
    target: "message",
    pattern: /Object Not Found Matching Id:\d+, MethodName:/i,
  },

  // -- Aborted network requests ---------------------------------------------
  { target: "message", pattern: /^AbortError:/i },
  { target: "message", pattern: /The operation was aborted/i },
  { target: "message", pattern: /The user aborted a request/i },
  { target: "message", pattern: /^cancell?ed$/i },
  { target: "message", pattern: /^cancelado$/i },
  {
    target: "message",
    pattern: /^(?:XHR|Fetch) error \w+ https?:\/\//i,
    statusCode: 0,
  },

  // -- Detached print iframe ------------------------------------------------
  {
    target: "message",
    pattern: /Failed to execute 'nextNode' on 'NodeIterator'/i,
  },

  // -- socket.io / engine.io polling-transport blips ------------------------
  { target: "message", pattern: /^Connection Error: /i },
  { target: "message", pattern: /xhr poll error/i },
  { target: "message", pattern: /xhr post error/i },
  { target: "message", pattern: /websocket error/i },

  // -- Code-split chunk load failure ----------------------------------------
  { target: "message", pattern: /Loading (CSS )?chunk \d+ failed/i },
  { target: "message", pattern: /ChunkLoadError/i },

  // -- Third-party SDK lifecycle --------------------------------------------
  { target: "message", pattern: /^Non-Error promise rejection captured/i },
];

type FilterableEvent = {
  url?: string;
  message?: string;
  statusCode?: number;
};

export const shouldDrop = (event: FilterableEvent): boolean => {
  for (const filter of NOISE_FILTERS) {
    if (
      filter.statusCode !== undefined &&
      event.statusCode !== filter.statusCode
    )
      continue;
    const value = filter.target === "url" ? event.url : event.message;
    if (!value) continue;
    if (filter.pattern.test(value)) return true;
  }
  return false;
};

// =============================================================================
// Axios error enrichment for `openReplayError`.
//
// The default `AxiosError: Request failed with status code 401` message is
// useless on its own — no method, no URL, no body, no correlation id.
// Stripping these fields out by hand at every call site is repetitive and
// error-prone, so `openReplayError` does it automatically when it sees an
// axios-shaped error.
// =============================================================================

const MAX_RESPONSE_BODY_CHARS = 1024;

const truncateString = (s: string, max: number): string =>
  s.length > max ? `${s.slice(0, max)}…(+${s.length - max} more chars)` : s;

const safeStringify = (value: unknown): string => {
  if (value == null) return "";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

type AxiosLikeError = {
  isAxiosError?: boolean;
  message?: string;
  code?: string;
  config?: {
    method?: string;
    url?: string;
    baseURL?: string;
    timeout?: number;
    __authUid?: string;
  };
  response?: {
    status?: number;
    statusText?: string;
    headers?: Record<string, string | undefined>;
    data?: unknown;
  };
};

const isAxiosLike = (error: unknown): error is AxiosLikeError => {
  if (typeof error !== "object" || error === null) return false;
  const e = error as Record<string, unknown>;
  return Boolean(e.isAxiosError) || ("config" in e && "response" in e);
};

const extractAxiosFields = (error: AxiosLikeError): Record<string, unknown> => {
  const { config, response } = error;
  const headers = response?.headers ?? {};
  const data = response?.data as Record<string, unknown> | undefined;

  const fullUrl =
    config?.baseURL && config.url
      ? `${config.baseURL.replace(/\/$/, "")}/${config.url.replace(/^\//, "")}`
      : config?.url;

  return {
    kind: "axios",
    method: config?.method?.toUpperCase(),
    url: scrubUrl(config?.url),
    fullUrl: scrubUrl(fullUrl),
    status: response?.status ?? 0,
    statusText: response?.statusText,
    code: error.code,
    timeoutMs: config?.timeout,
    requestId:
      headers["x-request-id"] ??
      headers["x-correlation-id"] ??
      headers["x-trace-id"],
    backendCode: data?.code ?? data?.errorCode,
    backendMessage: data?.message,
    responseBody: truncateString(safeStringify(data), MAX_RESPONSE_BODY_CHARS),
    // Stamped on the request by `onRequest` so a stale-after-impersonation
    // 401 is identifiable in the replay.
    authUid: config?.__authUid,
  };
};

let initialized = false;

/**
 * Initialise the OpenReplay tracker as early as possible (before React
 * mounts) so any error/page-load event during bootstrap is captured, same
 * as the previous Datadog setup. Active on every deployed env in
 * `ACTIVE_PROJECT_IDS` (live, migration, preproduction, staging — not local
 * dev, not sandbox); idempotent (safe to call from multiple entry points).
 *
 * Self-hosted on the free tier (no per-event billing), so capture is turned
 * up to the full feature set instead of the sampled setup Datadog needed:
 * full request/response payloads (redacted via `networkSanitizer`), long
 * animation tasks, live Assist (screen share / co-browsing / remote control
 * for support), and the function-level Profiler plugin (see
 * `openReplayProfile` below). console/exceptions/performance capture and
 * input masking are already full-on and privacy-safe by tracker default, so
 * they're left unconfigured here. Assist itself gates calls, remote control
 * and recording behind its own per-action user-confirmation popups by
 * default — nothing here bypasses that.
 *
 * Deliberately NOT using `@openreplay/tracker-axios` / `@openreplay/tracker-fetch`:
 * this core `network` config already patches `window.fetch` unconditionally
 * AND raw `XMLHttpRequest` (which is how axios 0.x actually sends requests
 * in a browser, and how Firestore's WebChannel long-poll transport talks),
 * so every axios/fetch/XHR call is already captured once. Adding either
 * plugin on top would double-log every request rather than add coverage.
 */
export const initOpenReplay = (): void => {
  if (initialized) return;
  if (!isActivateOpenReplay()) return;

  initialized = true;

  tracker.configure({
    projectKey: PROJECT_KEY as string,
    ingestPoint: INGEST_POINT,
    network: {
      // Maxed out for full tracking (self-hosted, no per-event cost):
      // every request/response captured with its payload, in iframes too,
      // proxy-based capture (more reliable than raw window patching).
      useProxy: true,
      captureInIframes: true,
      capturePayload: true,
      failuresOnly: false,
      // OFF: the tracker patches window.fetch/XMLHttpRequest globally with
      // no per-domain scoping, so this would stamp `X-OpenReplay-*` on
      // every outgoing request — including Firebase Auth's own calls to
      // identitytoolkit.googleapis.com / securetoken.googleapis.com — and
      // broke login with a 403. Re-enable only via `tokenUrlMatcher`
      // restricted to this app's own backend origin(s).
      sessionTokenHeader: false,
      // Hard security boundary, not a tracking knob: never capture auth
      // cookies/headers verbatim. Extends the tracker's own default
      // ['cookie', 'set-cookie', 'authorization'] with `access-token` —
      // this app's actual bearer-token header (see `onRequest` in
      // firebase.tsx), which isn't named "authorization" and was leaking
      // the live Firebase ID token into every captured request until this
      // was added. Match is case-insensitive (tracker lowercases both
      // sides), but keep this list exhaustive rather than relying on that.
      ignoreHeaders: ["cookie", "set-cookie", "authorization", "access-token"],
      sanitizer: networkSanitizer,
    },
    longTasks: true,
    // Rest of the "maxed out" knobs across other capture modules — all
    // no-cost on self-hosted, all off/coarser by default:
    mouse: {
      // Sample mouse position every tick (30ms) instead of the default
      // 210ms, for smoother cursor replay.
      trackingOffset: 1,
    },
    webAnimations: {
      // Default only tracks Web Animations on custom elements (Ionic
      // quirk); this app has none, so track every element instead.
      allElements: true,
    },
    css: {
      // Catches in-memory stylesheets created by @emotion (pulled in by
      // the MUI v5 components this app uses alongside MUI v4), which
      // otherwise don't show up correctly in replay.
      scanInMemoryCSS: true,
    },
  });
  tracker.use(trackerAssist());
  profile = tracker.use(trackerProfiler());
  // `start()` connects to `ingestPoint` and rejects if it's down/unreachable
  // — catch it so an OpenReplay outage surfaces as one console warning
  // instead of an unhandled promise rejection (which the app's own
  // error/RUM reporting would otherwise pick up as a real client error).
  tracker.start().catch((error) => {
    console.warn("OpenReplay failed to start", error);
  });
  // Baked into the bundle by vite.config.js — UTC timestamp plus short git
  // sha when CI provides one. Lets a replay be attributed to a specific
  // deploy. Requires "version"/"projectId" metadata fields configured in
  // project settings: https://docs.openreplay.com/en/installation/metadata/
  tracker.setMetadata("version", __APP_VERSION__);
  // Which env/backend this session belongs to (ffair-live, ffair-staging,
  // etc.) — same value gates activation above, so sessions are filterable
  // by env in the OpenReplay dashboard.
  tracker.setMetadata("projectId", import.meta.env.VITE_PROJECT_ID ?? "");
};

type Profile = ReturnType<ReturnType<typeof trackerProfiler>>;
let profile: Profile | undefined;

/**
 * Wrap a function so OpenReplay's Profiler plugin records its call
 * duration, arguments and return value in the session. Not applied to
 * anything by default — nothing in this codebase was slow enough to need
 * it yet — wrap a specific hot function with this when you do:
 *
 *   const compute = openReplayProfile("computeShowTotals", rawCompute);
 *
 * No-op (returns `fn` unchanged) when OpenReplay isn't active.
 */
export const openReplayProfile = <F extends (...args: never[]) => unknown>(
  name: string,
  fn: F,
  thisArg?: unknown
): F => {
  if (!isActivateOpenReplay() || !profile) return fn;
  return profile(name)(fn, thisArg) as F;
};

export const openReplayIdentify = (user: firebase.User | null): void => {
  if (!user || !isActivateOpenReplay()) return;
  tracker.identify(user.email ?? user.uid);
};

/**
 * Attach business context (role, organiser, current show) to the current
 * OpenReplay session, so sessions can be filtered by role/show in the
 * dashboard once the metadata keys below are configured in project settings:
 * https://docs.openreplay.com/en/installation/metadata/
 *
 * Pass `null` for any field to clear it. OpenReplay metadata has no removal
 * call (ponytail: ceiling — `setMetadata` only accepts strings, so `null`
 * is written as `""`; revisit if OpenReplay adds a proper unset API).
 */
export const setOpenReplayContext = (ctx: ObservabilityContext): void => {
  if (!isActivateOpenReplay()) return;
  for (const [key, value] of Object.entries(ctx)) {
    if (value !== undefined) {
      tracker.setMetadata(key, value ?? "");
    }
  }
};

export const openReplayLogout = (): void => {
  if (!isActivateOpenReplay()) return;
  tracker.setUserID("");
};

/**
 * Record a custom user action — login attempts, checkout milestones, file
 * uploads, etc. Shows up as a session event in the OpenReplay dashboard.
 */
export const openReplayAction = (
  name: string,
  context?: Record<string, unknown>
): void => {
  if (!isActivateOpenReplay()) return;
  tracker.event(name, context);
};

/**
 * Redux middleware that records every dispatched action as an OpenReplay
 * session event, so a replay shows what the app *did*, not just what
 * network calls it made. Payload is redacted the same way as network
 * bodies (`redactBody`) before being sent.
 *
 * Deliberately NOT using the official `@openreplay/tracker-redux` plugin:
 * that plugin's `tracker.use(...)` binds permanently to whatever tracker
 * state exists at the moment it's called (see `use()` in the SDK — it's
 * `fn(null)` and done if the tracker isn't configured/started yet), while
 * `configureStore()` in `store/store.ts` runs at module-import time, before
 * `initOpenReplay()` is ever called from `index.tsx`. Wiring the plugin
 * would have silently recorded nothing forever. `openReplayAction` doesn't
 * have that problem — it re-checks activation on every call — so this
 * middleware calls it directly instead.
 */
export const openReplayReduxMiddleware: Middleware = () => (next) => (
  action: AnyAction
) => {
  try {
    openReplayAction(
      typeof action?.type === "string" ? action.type : "redux/unknown",
      redactBody(action) as Record<string, unknown>
    );
  } catch (error) {
    console.warn("Failed to record Redux action to OpenReplay", error);
  }
  return next(action);
};

/**
 * Record a custom timing for performance-sensitive operations (e.g. how
 * long a checkout / upload takes), in milliseconds.
 *
 * ponytail: ceiling — the tracker has no dedicated timing API, so this is
 * just a named event carrying `durationMs`. Upgrade if OpenReplay adds one.
 */
export const openReplayTiming = (name: string, durationMs: number): void => {
  if (!isActivateOpenReplay()) return;
  tracker.event(name, { durationMs });
};

/**
 * Send a handled error to OpenReplay. Use for caught exceptions where the
 * user sees a graceful fallback but we still want the failure recorded
 * against the session replay.
 *
 * Axios errors are auto-enriched with method / URL / status / request-id /
 * backend code / truncated response body — callers normally just pass
 * `{ source: "..." }`. Caller-supplied context wins over auto-extracted
 * fields.
 */
export const openReplayError = (
  error: unknown,
  context?: Record<string, unknown>
): void => {
  if (!isActivateOpenReplay()) return;
  const err = error instanceof Error ? error : new Error(String(error));
  const enriched: Record<string, unknown> = isAxiosLike(error)
    ? { ...extractAxiosFields(error), ...context }
    : { ...context };

  if (
    shouldDrop({
      message: err.message,
      statusCode: enriched.status as number | undefined,
    })
  ) {
    return;
  }

  tracker.handleError(err, enriched);
};
